// Página de Planes y Recargas
// Accesible desde el sidebar > Planes
// Tiene dos tabs: Membresía (Suscripciones) y Recarga Única (Postpago)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePayment } from './usePayment';
import {
    CreditCard, Shield, ArrowRight, Calculator,
    Check, Star, Sparkles, ArrowLeft, Loader2, Clock
} from 'lucide-react';
import { PREPAGO_PLANS, formatCOP } from './plans';
import { useGeoPricing } from './hooks/useGeoPricing';
import { InternationalOneTimeCardForm } from './components/recurring/InternationalOneTimeCardForm';

import { SubscriptionManager } from './components/recurring/SubscriptionManager';
import { PaymentMethodsList } from './components/recurring/PaymentMethodsList';
import { BillingHistory } from './components/recurring/BillingHistory';
import { toast } from 'sonner';
import { OneTimePaymentForm } from './components/recurring/OneTimePaymentForm';
import { recurringPaymentsApi } from '../../services/recurringPaymentsApi';
import type { Subscription, PaymentMethod, BillingAttempt } from '../../types/payments';
import { errorCode, paymentCloseMessage, userErrorMessage } from '../../lib/userErrors';
import { reviewPendingPayment, usePaymentReviewPoll } from './usePaymentReviewPoll';

const PLAN_LABELS: Record<string, string> = {
    basic_200: 'Básico',
    standard_1000: 'Estándar',
    premium_3000: 'Avanzado',
};

const QUICK_AMOUNTS = [
    { label: '$200,000', value: 200000 },
    { label: '$300,000', value: 300000 },
    { label: '$500,000', value: 500000 },
    { label: '$1,000,000', value: 1000000 },
];

export function PaymentCheckoutPage() {
    const navigate = useNavigate();
    const [tab, setTab] = useState<'membresia' | 'recarga'>('membresia');
    const [amount, setAmount] = useState<string>('');
    const [checkoutPlan, setCheckoutPlan] = useState<{ id: string, name: string, price: number } | null>(null);
    const { pay, loading: webCheckoutLoading, calculateMessages, pricePerMessage, internationalEnabled } = usePayment();
    const {
        geoConfig, formatPrice, getPlanConfig, getPostpagoPrice, getPostpagoMinRecharge, currency, countryOverride,
    } = useGeoPricing();
    const chargeUsd = Boolean(geoConfig?.is_international && internationalEnabled);
    const formatMoney = (value: number) => (chargeUsd ? formatPrice(value) : formatCOP(value));
    const plans = PREPAGO_PLANS.map((plan) => {
        if (!chargeUsd) return plan;
        const cfg = getPlanConfig(plan.id);
        if (!cfg) return plan;
        return { ...plan, price: cfg.price, messages: cfg.messages ?? plan.messages };
    });
    const [intlAmount, setIntlAmount] = useState('');

    const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [billingAttempts, setBillingAttempts] = useState<BillingAttempt[]>([]);
    const [cardsCount, setCardsCount] = useState<number>(0);
    const [defaultCardId, setDefaultCardId] = useState<string | null>(null);
    const [loadingData, setLoadingData] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [openAddFormRequest, setOpenAddFormRequest] = useState(0);
    const [localPendingPlan, setLocalPendingPlan] = useState<string | null>(null);
    const [declinedNotice, setDeclinedNotice] = useState<string | null>(null);

    const numericAmount = parseFloat(amount.replace(/\D/g, '')) || 0;
    const messages = calculateMessages(numericAmount);

    const loadUserData = async (force = false) => {
        setLoadingData(true);
        try {
            const [subRes, cardsRes, historyRes] = await Promise.all([
                recurringPaymentsApi.getSubscription({ force }),
                recurringPaymentsApi.listCards({ force }),
                recurringPaymentsApi.getBillingHistory(15, { force }),
            ]);
            setHasSubscription(subRes.has_subscription);
            setSubscription(subRes.has_subscription ? subRes.subscription : null);
            if (subRes.subscription?.status === 'active') {
                setDeclinedNotice(null);
                setLocalPendingPlan(null);
            } else if (!subRes.subscription && subRes.payment_result) {
                setDeclinedNotice(paymentCloseMessage(subRes.payment_result.code));
            }
            setPaymentMethods(cardsRes);
            setBillingAttempts(historyRes.attempts);
            setCardsCount(cardsRes.length);
            const defaultCard = cardsRes.find(c => c.is_default);
            if (defaultCard) setDefaultCardId(defaultCard.id);
            else if (cardsRes.length > 0) setDefaultCardId(cardsRes[0].id);
            else setDefaultCardId(null);
        } catch (err) {
            console.error("Error cargando estado inicial", err);
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        loadUserData();
    }, []);

    const refreshUserData = () => loadUserData(true);

    const pendingPlanName = localPendingPlan
        || (subscription?.status === 'pending_first_payment'
            ? (PLAN_LABELS[subscription.plan_type] || subscription.plan_type)
            : null);
    const cardPhoneCountry = chargeUsd ? (geoConfig?.country_code || 'CO') : 'CO';

    usePaymentReviewPoll(
        Boolean(pendingPlanName),
        () => {
            toast.success('Pago aprobado', { description: 'Tu plan ya está activo.' });
            setLocalPendingPlan(null);
            setDeclinedNotice(null);
            refreshUserData();
        },
        (message) => {
            setLocalPendingPlan(null);
            setDeclinedNotice(message);
            refreshUserData();
        },
    );

    const recheckPending = async () => {
        try {
            const outcome = await reviewPendingPayment();
            if (outcome === 'pending') {
                toast.info('Tu pago sigue en revisión', { description: 'Puede tardar unos minutos.' });
                return;
            }
            if (outcome === 'active') {
                toast.success('Pago aprobado', { description: 'Tu plan ya está activo.' });
                setLocalPendingPlan(null);
                setDeclinedNotice(null);
            } else {
                setLocalPendingPlan(null);
                setDeclinedNotice(outcome.declined);
            }
            refreshUserData();
        } catch (err) {
            toast.error(userErrorMessage(err, 'No pudimos revisar el estado del pago. Intenta de nuevo.'));
        }
    };

    const handleConfirmSubscription = async () => {
        if (!checkoutPlan) return;

        if (cardsCount === 0) {
            setOpenAddFormRequest((prev) => prev + 1);

            toast.error("Método de pago requerido", {
                description: "Primero debes añadir una tarjeta."
            });

            return;
        }

        if (!defaultCardId) {
            toast.error("Selecciona una tarjeta", {
                description: "No hay una tarjeta seleccionada para el pago."
            });
            return;
        }

        setSubscribing(true);
        try {
            if (chargeUsd) {
                await recurringPaymentsApi.createInternationalSubscription({
                    plan_type: checkoutPlan.id as any,
                    payment_method_id: defaultCardId,
                }, countryOverride || undefined);
            } else {
                await recurringPaymentsApi.createSubscription({
                    plan_type: checkoutPlan.id as any,
                    payment_method_id: defaultCardId,
                });
            }
            toast.success("¡Suscripción exitosa!", {
                description: "Tu plan ha sido activado y el cobro se realizó correctamente.",
                duration: 5000,
            });
            setCheckoutPlan(null);
            setDeclinedNotice(null);
            await refreshUserData(); // Recargar estado para mostrar el dashboard de suscripción
        } catch (err: any) {
            console.error(err);
            if (errorCode(err) === 'payment_pending') {
                setDeclinedNotice(null);
                setLocalPendingPlan(checkoutPlan.name);
                return;
            }
            toast.error("Error en la suscripción", {
                description: userErrorMessage(err, "No pudimos procesar tu suscripción en este momento.")
            });
        } finally {
            setSubscribing(false);
        }
    };

    return (
    <div className="min-h-screen bg-white flex flex-col items-center py-6 px-4 transition-colors overflow-x-hidden">
        <div className="w-full max-w-[1100px] relative z-10 origin-top transform scale-[0.85] sm:scale-90 md:scale-[0.85]">

            {/* Header & Botón Atrás */}
            <div className="relative w-full mb-6 text-center flex flex-col items-center pt-2">
                <div className="absolute left-0 sm:left-4 top-1.5 z-20">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-sm font-semibold text-slate-600 hover:text-slate-900 transition-all bg-white hover:bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-200 shadow-sm group"
                    >
                        <ArrowLeft className="w-5 h-5 mr-1.5 group-hover:-translate-x-1 transition-transform" />
                        Atrás
                    </button>
                </div>

                <div className="w-14 h-14 bg-[#E8F7F7] rounded-2xl flex items-center justify-center mb-3 border border-[#CDEEEE]">
                    <CreditCard className="w-7 h-7 text-[#1CB3B3]" />
                </div>

                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                    Pagos y Facturación
                </h1>

                <p className="text-slate-500 text-sm max-w-md mx-auto">
                    Gestiona tus suscripciones, métodos de pago y recargas.
                </p>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-1.5 bg-slate-100 border border-slate-200 rounded-xl mb-10 w-fit mx-auto">
                <button
                    onClick={() => setTab('membresia')}
                    className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        tab === 'membresia'
                            ? 'bg-[#1CB3B3] text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-800 hover:bg-white'
                    }`}
                >
                    Membresía
                </button>

                <button
                    onClick={() => setTab('recarga')}
                    className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        tab === 'recarga'
                            ? 'bg-[#1CB3B3] text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-800 hover:bg-white'
                    }`}
                >
                    Recarga Única
                </button>
            </div>

                {/* ─── Membresía (Suscripciones) ─────────────────────────── */}
                {tab === 'membresia' && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {loadingData ? (
                            <div className="flex justify-center p-12">
                                <Loader2 className="w-10 h-10 animate-spin text-[#1CB3B3]" />
                            </div>
                        ) : pendingPlanName ? (
                            <div className="max-w-lg mx-auto bg-white border border-amber-200 rounded-2xl p-8 text-center shadow-sm">
                                <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 text-amber-500 flex items-center justify-center mb-4">
                                    <Clock className="w-7 h-7" />
                                </div>
                                <h2 className="text-xl font-bold text-slate-900 mb-2">Tu pago está en revisión</h2>
                                <p className="text-sm text-slate-600 mb-6">
                                    El plan {pendingPlanName} puede tardar unos minutos. No vuelvas a pagar ni elimines
                                    la tarjeta: cuando tu banco responda, esta página se actualiza sola.
                                </p>
                                <button
                                    type="button"
                                    onClick={recheckPending}
                                    className="w-full bg-[#1CB3B3] hover:bg-[#169a9a] text-white font-bold py-3.5 px-4 rounded-xl"
                                >
                                    Revisar de nuevo
                                </button>
                            </div>
                        ) : (
                            <>
                                {declinedNotice && (
                                    <div className="max-w-[1000px] mx-auto mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
                                        {declinedNotice}
                                    </div>
                                )}
                                {hasSubscription ? (
                                    /* Si YA tiene suscripción, aumentar espacio lateral a 2:1 (60/40) */
                                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                                        <div className="col-span-1 lg:col-span-3 space-y-8">
                                            <SubscriptionManager
                                                initialSubscription={subscription}
                                                onChanged={refreshUserData}
                                            />
                                            <BillingHistory initialAttempts={billingAttempts} />
                                        </div>
                                        <div className="col-span-1 lg:col-span-2 space-y-6">
                                            <PaymentMethodsList
                                                initialMethods={paymentMethods}
                                                onDataChanged={refreshUserData}
                                                international={chargeUsd}
                                                phoneCountry={cardPhoneCountry}
                                                cardsLocked={Boolean(pendingPlanName)}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    /* Si NO tiene suscripción */
                                    <>
                                        {checkoutPlan ? (
                                            /* Vista Checkout: Información a la izquierda, Tarjeta a la derecha */
                                            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 max-w-[1100px] mx-auto">
                                                <div className="col-span-1 lg:col-span-3 space-y-6">
                                                    <button
                                                        onClick={() => setCheckoutPlan(null)}
                                                        className="text-slate-500 mb-4 flex items-center hover:text-slate-900 transition-colors text-sm font-medium"
                                                    >
                                                        <ArrowLeft className="w-4 h-4 mr-1.5" />
                                                        Volver a planes
                                                    </button>

                                                    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">

                                                        <div className="flex items-center gap-3 mb-2">
                                                            <div className="p-2 bg-[#E8F7F7] rounded-xl">
                                                                <Star className="w-5 h-5 text-[#1CB3B3]" />
                                                            </div>

                                                            <h2 className="text-xl font-bold text-slate-900">
                                                                Resumen de tu suscripción
                                                            </h2>
                                                        </div>

                                                        <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 mt-6">
                                                            <h3 className="text-lg text-slate-900 font-semibold flex items-center justify-between">
                                                                Plan {checkoutPlan.name}

                                                                <span className="text-sm font-normal text-slate-500">
                                                                    Recurrente mensual
                                                                </span>
                                                            </h3>

                                                            <p className="text-3xl text-slate-900 font-extrabold mt-2">
                                                                {formatMoney(checkoutPlan.price)}

                                                                <span className="text-base font-medium text-slate-500">
                                                                    {chargeUsd ? ` ${currency} / mes` : ' / mes'}
                                                                </span>
                                                            </p>
                                                        </div>

                                                        <p className="text-slate-500 text-sm mt-5 leading-relaxed">
                                                            Al confirmar, se realizará un cargo recurrente a tu tarjeta seleccionada correspondiente por el valor de este plan.
                                                            Podrás cancelar en cualquier momento desde esta misma página.
                                                        </p>

                                                        <hr className="my-6 border-slate-200" />

                                                        <button
                                                            onClick={handleConfirmSubscription}
                                                            disabled={subscribing}
                                                            className="w-full bg-[#1CB3B3] hover:bg-[#169a9a] active:scale-[0.98] text-white font-bold py-3.5 px-4 rounded-xl flex justify-center items-center transition-all disabled:opacity-50"
                                                        >
                                                            {subscribing
                                                                ? <Loader2 className="w-5 h-5 animate-spin" />
                                                                : "Confirmar y Suscribirse"
                                                            }
                                                        </button>

                                                    </div>
                                                </div>
                                                <div className="col-span-1 lg:col-span-2">
                                                    <PaymentMethodsList
                                                        initialMethods={paymentMethods}
                                                        onDataChanged={refreshUserData}
                                                        international={chargeUsd}
                                                        phoneCountry={cardPhoneCountry}
                                                        cardsLocked={Boolean(pendingPlanName)}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            /* Vista de Planes Centrada */
                                            <div className="max-w-[1000px] mx-auto space-y-8">
                                                <div className="text-center mb-8">
                                                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Elige un plan</h2>
                                                    <p className="text-slate-500 text-sm">Selecciona un plan diseñado para tus necesidades. Cancela cuando quieras.</p>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                    {plans.map((plan) => (
                                                        <div
                                                            key={plan.id}
                                                            className={`relative rounded-2xl border ${plan.border} ${plan.cardBg} flex flex-col overflow-hidden shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300`}
                                                        >
                                                            {plan.badge && (
                                                                <div className="absolute top-4 right-4 z-10">
                                                                    <span className="flex items-center gap-1 text-[10px] font-bold text-[#86BC2A] bg-[#F2F8E7] border border-[#D7EEAE] px-2.5 py-1 rounded-full">
                                                                        <Sparkles className="w-3 h-3" />
                                                                        {plan.badge}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            <div className="p-6 flex flex-col">
                                                                <div
                                                                    className={`w-10 h-10 ${plan.iconBg} ${plan.textColor} rounded-xl flex items-center justify-center mb-4`}
                                                                >
                                                                    {plan.icon}
                                                                </div>

                                                                <div className="font-bold text-lg text-slate-900 mb-2">
                                                                    {plan.name}
                                                                </div>

                                                                <div className="flex items-baseline gap-1">
                                                                    <span className="text-4xl font-extrabold text-slate-900">
                                                                        {formatMoney(plan.price)}
                                                                    </span>
                                                                    <span className="text-slate-500 text-sm font-medium">
                                                                        /mes
                                                                    </span>
                                                                </div>

                                                                <div className="mt-2 text-sm font-medium text-slate-600">
                                                                    {plan.messages.toLocaleString('es-CO')} recordatorios automáticos incluidos
                                                                </div>
                                                            </div>

                                                            <div className="px-6 pb-6 flex-1 flex flex-col">
                                                                <div className="border-t border-slate-200 mb-5" />

                                                                <ul className="space-y-3 mb-6 flex-1">
                                                                    {plan.features.map((f, i) => (
                                                                        <li
                                                                            key={i}
                                                                            className="flex items-start gap-2.5 text-sm text-slate-700 font-medium tracking-tight"
                                                                        >
                                                                            <Check
                                                                                className={`w-4 h-4 ${plan.textColor} shrink-0 mt-0.5`}
                                                                                strokeWidth={3}
                                                                            />
                                                                            {f}
                                                                        </li>
                                                                    ))}
                                                                </ul>

                                                                <button
                                                                    onClick={() =>
                                                                        setCheckoutPlan({
                                                                            id: plan.id,
                                                                            name: plan.name,
                                                                            price: plan.price,
                                                                        })
                                                                    }
                                                                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white transition-all shadow-sm active:scale-[0.98] ${plan.color} hover:opacity-90 mt-auto`}
                                                                >
                                                                    Suscribirse
                                                                    <ArrowRight className="w-4 h-4 ml-1" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ─── Recarga Única (Postpago) ────────────────────────────── */}
                {tab === 'recarga' && chargeUsd && (
                    <div className="max-w-[900px] mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">
                        <p className="text-sm text-slate-500 text-center">
                            Recarga en dólares. El pago se procesa con ePayco y los mensajes no vencen.
                            <br />
                            <span className="mt-3 inline-block text-[#159A9C] font-semibold bg-[#E8F7F7] px-4 py-1.5 rounded-full border border-[#CDEEEE]">
                                Precio: {formatPrice(getPostpagoPrice())} {currency} / recordatorio
                            </span>
                        </p>
                        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
                            <label className="text-sm font-bold text-slate-900 block mb-2">
                                Monto ({currency})
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={intlAmount}
                                onChange={(e) => {
                                    let val = e.target.value.replace(/[^0-9.]/g, '');
                                    const parts = val.split('.');
                                    if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
                                    setIntlAmount(val);
                                }}
                                placeholder="0.00"
                                className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 text-xl font-bold outline-none focus:border-[#1CB3B3] focus:ring-2 focus:ring-[#1CB3B3]/10"
                            />
                            <div className="mt-6">
                                <InternationalOneTimeCardForm
                                    amount={parseFloat(intlAmount) || 0}
                                    planType="postpago"
                                    currency={currency}
                                    minRecharge={getPostpagoMinRecharge()}
                                    countryOverride={countryOverride || undefined}
                                    onSuccess={() => {
                                        toast.success('Pago recibido');
                                        setIntlAmount('');
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {tab === 'recarga' && !chargeUsd && (
                    <div className="max-w-[900px] mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">

                        <p className="text-sm text-slate-500 mb-8 text-center">
                            Recarga el monto que quieras mediante tus cuentas bancarias. Los mensajes no vencen.
                            <br />

                            <span className="mt-3 inline-block text-[#159A9C] font-semibold bg-[#E8F7F7] px-4 py-1.5 rounded-full border border-[#CDEEEE]">
                                Precio: ${pricePerMessage} COP / recordatorio
                            </span>
                        </p>

                        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8 space-y-6 mb-6">

                            {/* Montos rápidos */}
                            <div>
                                <p className="text-sm font-bold text-slate-900 mb-3">
                                    Montos frecuentes
                                </p>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {QUICK_AMOUNTS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            onClick={() => setAmount(String(opt.value))}
                                            className={`py-3 rounded-xl text-sm font-semibold transition-all border ${
                                                numericAmount === opt.value
                                                    ? 'bg-[#E8F7F7] border-[#1CB3B3] text-[#159A9C]'
                                                    : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Input y Calculadora */}
                            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">

                                <div className="flex-1 w-full">
                                    <label className="text-sm font-bold text-slate-900 block mb-2">
                                        Monto personalizado
                                    </label>

                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-700 font-semibold text-lg">
                                            $
                                        </span>

                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            value={amount}
                                            onChange={(e) =>
                                                setAmount(e.target.value.replace(/\D/g, ''))
                                            }
                                            placeholder="0"
                                            className="w-full pl-10 pr-16 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 text-xl font-bold outline-none focus:border-[#1CB3B3] focus:ring-2 focus:ring-[#1CB3B3]/10 transition-all"
                                        />

                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                                            COP
                                        </span>
                                    </div>
                                </div>

                                {messages > 0 && (
                                    <div className="flex-shrink-0 bg-[#E8F7F7] border border-[#CDEEEE] rounded-2xl p-4 flex flex-col justify-center min-w-[140px]">
                                        <span className="text-[10px] font-bold text-[#159A9C] uppercase">
                                            Obtienes
                                        </span>

                                        <div className="flex items-baseline gap-1.5">
                                            <span className="text-2xl font-black text-slate-900">
                                                {messages.toLocaleString('es-CO')}
                                            </span>

                                            <span className="text-[10px] text-slate-500">
                                                msj
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Payment Selection Web Checkout Tradicional */}
                        {numericAmount >= 200000 ? (
                            <button
                                onClick={() => pay(numericAmount, 'postpago')}
                                disabled={webCheckoutLoading || numericAmount < 200000}
                                className="w-full bg-[#1CB3B3] hover:bg-[#169a9a] active:scale-[0.98] text-white font-bold py-4 px-6 rounded-xl flex justify-center items-center gap-2 shadow-sm transition-all disabled:opacity-50"
                            >
                                {webCheckoutLoading ? (
                                    <Loader2 className="w-6 h-6 animate-spin" />
                                ) : (
                                    <>
                                        Proceder a pagar con ePayco <ArrowRight className="w-5 h-5" />
                                        <ArrowRight className="w-5 h-5" />
                                    </>
                                )}
                            </button>
                        ) : (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                                <p className="text-amber-700 text-sm font-bold">
                                    Monto mínimo: $200,000 COP
                                </p>

                                <p className="text-amber-600 text-xs mt-1">
                                    Requerido por la pasarela de pago
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default PaymentCheckoutPage;
